import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSportsData } from "../App";
import useDebounce from "../hooks/useDebounce";

// Small helper component to lazy-load CSS background images when the card actually enters viewport.
const LazyBackground = ({ src, className = "", height = "60%" }) => {
    const ref = useRef(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            ([entry], obs) => {
                if (entry.isIntersecting) {
                    setLoaded(true);
                    obs.disconnect();
                }
            },
            { rootMargin: "100px" }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={className}
            style={{
                backgroundImage: loaded ? `url(${src})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                height,
            }}
        />
    );
};

// Background images are served from the public folder to avoid bundling them in JS
// Using URLs instead of static imports reduces bundle size.

const TourPackages = ({ }) => {
    const { data: sports = [], isLoading: sportsLoading, error } = useSportsData();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false); // Initialize as false until we start fetching
    const [initialFetchStarted, setInitialFetchStarted] = useState(false); // Track if initial fetch has started
    const [offset, setOffset] = useState(0);
    const sectionRef = useRef(null);
    const [sectionVisible, setSectionVisible] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const scrollContainerRef = useRef(null);
    const isFetchingRef = useRef(false);
    const navigate = useNavigate();

    // Array of background image URLs served from the public folder
    const backgroundImages = [
        "/assets/images/image1.jpeg",
        "/assets/images/image2.jpeg",
        "/assets/images/image3.jpeg",
        "/assets/images/image4.jpeg",
        "/assets/images/image5.jpg",
        "/assets/images/image6.jpeg",
        "/assets/images/image7.jpeg",
        "/assets/images/image8.jpg",
        "/assets/images/image9.jpg",
    ];

    const fetchData = async (offset) => {
        if (isFetchingRef.current) return; // prevent overlapping calls
        
        if (!initialFetchStarted) {
            setLoading(true);
            setInitialFetchStarted(true);
        }
        
        isFetchingRef.current = true;
        
        try {
            const apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/search-filter/current-upcoming/`;
            
            const requestBody = {
                timeline_status: "current",
                entity: "courses",
                limit: 10,
                offset: offset
            };
            
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    accept: "application/json",
                },
                body: JSON.stringify(requestBody),
            });
            
            if (response.ok) {
                const data = await response.json();
                
                if (data.length > 0) {
                    setPackages(prevPackages => [
                        ...prevPackages,
                        ...data
                    ]);
                    setOffset(offset + 10);
                } else {
                    setHasMore(false);
                }
            }
        } catch (error) {
            // Error handling without logging
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    };

    const handleViewProviders = async (course) => {
        try {
            const params = new URLSearchParams();
            params.append('sport', course.sport_id);
            params.append('course', course.course_id);

            navigate(`/search-results?${params.toString()}`);
        } catch (error) {
            alert("Error navigating to providers. Please try again.");
        }
    };



    // Observe section visibility and trigger initial fetch lazily
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry], obs) => {
                if (entry.isIntersecting) {
                    setSectionVisible(true);
                    obs.disconnect();
                }
            },
            { rootMargin: "200px" }
        );
        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (sectionVisible && packages.length === 0 && !initialFetchStarted) {
            fetchData(0);
        }
    }, [sectionVisible, initialFetchStarted, packages.length]);

    // Debounce scroll events to prevent excessive API calls
    const handleScroll = useCallback(() => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            if (scrollWidth - scrollLeft <= clientWidth + 100 && hasMore && !isFetchingRef.current) {
                fetchData(offset);
            }
        }
    }, [offset, hasMore]);

    // Create debounced version of scroll handler
    const debouncedHandleScroll = useDebounce(handleScroll, 300);
    
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            // Use the debounced scroll handler to prevent rapid firing of API calls
            const scrollListener = () => debouncedHandleScroll();
            scrollContainer.addEventListener('scroll', scrollListener, { passive: true });
            return () => scrollContainer.removeEventListener('scroll', scrollListener);
        }
    }, [debouncedHandleScroll]);

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: -300,
                behavior: 'smooth',
            });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: 300,
                behavior: 'smooth',
            });
        }
    };



    if (loading && packages.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                <div className="ml-4 text-sm text-gray-500">Loading courses...</div>
            </div>
        );
    }

    return (
        <section id="tour_packages" ref={sectionRef} className="bg-white w-full pt-4 md:pt-8">
            <div className="mx-auto w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-28">
                <div className="text-center mb-6 sm:mb-8">
                    <h2 className="section-title">
                        Upcoming Courses & Activities
                    </h2>
                    <p className="section-subtitle">
                        Choose your next adventure from our expert-led courses
                    </p>
                </div>

                <div className="relative">
                    <button
                        onClick={scrollLeft}
                        className="hidden sm:flex absolute left-0 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 rounded-full shadow-lg hover:bg-opacity-100 transition-all z-10 items-center justify-center w-8 h-8 sm:w-10 sm:h-10 left-0 sm:-left-4"
                        aria-label="Scroll left"
                    >
                        &lt;
                    </button>
                    <button
                        onClick={scrollRight}
                        className="hidden sm:flex absolute right-0 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 rounded-full shadow-lg hover:bg-opacity-100 transition-all z-10 items-center justify-center w-8 h-8 sm:w-10 sm:h-10 right-0 sm:-right-4"
                        aria-label="Scroll right"
                    >
                        &gt;
                    </button>

                    <div
                        className="overflow-x-auto pb-4 hide-scrollbar"
                        ref={scrollContainerRef}
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        <div className="flex space-x-4 sm:space-x-6 pl-2 sm:pl-0">
                            {packages.map((course, index) => {


                                //const backgroundImage = `https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload/${course.image_public_id}`;
                                const backgroundImage = `https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload/c_fill,g_auto,f_auto,q_auto/${course.image_public_id}`;

                                // const backgroundImage = backgroundImages[index % backgroundImages.length];

                                return (
                                    <div
                                        key={index}
                                        className="flex-shrink-0 w-64 sm:w-72 md:w-80 aspect-[3/4] bg-white rounded-lg sm:rounded-xl overflow-hidden border border-gray-200 shadow-md flex flex-col"
                                    >

                                        <LazyBackground src={backgroundImage} className="flex-none"  />

                                        <div className="p-3 sm:p-4 flex flex-col justify-between flex-grow" style={{ height: '40%' }}>
                                            <div className="flex-grow">
                                                <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-1 sm:mb-2 line-clamp-2">
                                                    {(() => {
                                                        const sport = sports.find(s => s.sport_id === course.sport_id);
                                                        const courseData = sport?.courses?.find(c => c.course_id === course.course_id);
                                                        return `${sport?.name || 'Unknown Sport'} - ${courseData?.name || 'Unknown'} Course`;
                                                    })()}
                                                </h3>

                                                <p className="text-xs sm:text-sm text-neutral-600 mb-2 sm:mb-3">
                                                    {course.skill_level.charAt(0).toUpperCase() + course.skill_level.slice(1)}
                                                </p>
                                            </div>

                                            <button
                                                className="mt-auto w-full bg-primary text-white py-1 sm:py-2 rounded-md hover:bg-gray-700 transition-all text-sm sm:text-base"
                                                onClick={() => handleViewProviders(course)}
                                            >
                                                View All Providers
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TourPackages;